/**
 * Escala categórica de diâmetro das tubulações e válvulas INP (ambas usam a
 * mesma escala e legenda). Cada valor distinto ganha uma cor própria — sem
 * interpolar como Pressão/Cota. A cor fica presa ao número (100 vermelho
 * continua 100 mesmo se sumir do mapa). Até 10 tipos usam a paleta fixa na
 * primeira ocorrência; acima disso, cores extras. No basemap claro usa as
 * cores vivas da Cota/Pressão.
 */

import {
  coresDiametroSalvas,
  gravarCoresDiametro,
} from '@/utils/mapa-escalas-cores-prefs'

export const CHAVE_PIPES = 'pipes'
export const CHAVE_VALVES = 'valves'
export const PROP_DIAMETRO = 'Diâmetro'

// Seções INP coloridas pela escala de diâmetro (linha). Tubulações e válvulas
// compartilham a escala, a legenda e a propriedade Diâmetro.
export function ehChaveDiametro(chave) {
  return chave === CHAVE_PIPES || chave === CHAVE_VALVES
}

export const PALETA_DIAMETRO = [
  '#C62828',
  '#1565C0',
  '#2E7D32',
  '#6A1B9A',
  '#E65100',
  '#00838F',
  '#4E342E',
  '#AD1457',
  '#9E9D24',
  '#3949AB',
]

export const PALETA_DIAMETRO_CLARO = [
  '#FF1744',
  '#3388FF',
  '#64DD17',
  '#AA00FF',
  '#FFA000',
  '#00B8D4',
  '#1A237E',
  '#F50057',
  '#FFD600',
  '#7C4DFF',
]

function paletaDiametro(escuro) {
  return escuro ? PALETA_DIAMETRO : PALETA_DIAMETRO_CLARO
}

const ANGULO_OURO = 137.508
const DIST_HUE_MIN = 18

export function parseDiametro(valor) {
  if (valor == null || valor === '') {
    return null
  }

  const n = Number(String(valor).replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

export function formatarDiametro(valor) {
  if (!Number.isFinite(valor)) {
    return ''
  }
  if (Math.abs(valor - Math.round(valor)) < 1e-9) {
    return String(Math.round(valor))
  }
  return Number(valor).toFixed(1)
}

export function diametrosNasCamadas(camadas) {
  const unicos = new Set()
  ;(camadas || []).forEach((camada) => {
    if (
      camada.visivel === false ||
      !ehChaveDiametro(camada.chave) ||
      !camada.geojson
    ) {
      return
    }

    ;(camada.geojson.features || []).forEach((feature) => {
      const n = parseDiametro(
        feature.properties && feature.properties[PROP_DIAMETRO]
      )
      if (n == null) {
        return
      }
      unicos.add(n)
    })
  })

  return Array.from(unicos).sort((a, b) => a - b)
}

function hexParaHsl(hex) {
  const n = String(hex).replace('#', '')
  const r = parseInt(n.slice(0, 2), 16) / 255
  const g = parseInt(n.slice(2, 4), 16) / 255
  const b = parseInt(n.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) {
    return [0, 0, l]
  }

  const s = d / (1 - Math.abs(2 * l - 1))
  let h
  if (max === r) {
    h = ((g - b) / d) % 6
  } else if (max === g) {
    h = (b - r) / d + 2
  } else {
    h = (r - g) / d + 4
  }

  return [(((h * 60) % 360) + 360) % 360, s, l]
}

function hslParaHex(h, s, l) {
  const hh = ((h % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0
  if (hh < 60) {
    r = c
    g = x
  } else if (hh < 120) {
    r = x
    g = c
  } else if (hh < 180) {
    g = c
    b = x
  } else if (hh < 240) {
    g = x
    b = c
  } else if (hh < 300) {
    r = x
    b = c
  } else {
    r = c
    b = x
  }

  const hex = (n) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${hex(r)}${hex(g)}${hex(b)}`
}

function distHue(a, b) {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}

function hueLivre(hue, huesUsados) {
  return huesUsados.every((usado) => distHue(usado, hue) >= DIST_HUE_MIN)
}

function gerarCorExtra(indice, huesUsados, escuro) {
  let i = indice
  for (let tentativa = 0; tentativa < 36; tentativa += 1) {
    const hue = (i * ANGULO_OURO) % 360
    const light = escuro ? 0.32 + (i % 3) * 0.05 : 0.48 + (i % 3) * 0.05
    if (hueLivre(hue, huesUsados)) {
      huesUsados.push(hue)
      return hslParaHex(hue, 0.88, light)
    }
    i += 1
  }

  const hue = (i * ANGULO_OURO) % 360
  huesUsados.push(hue)
  return hslParaHex(hue, 0.88, escuro ? 0.37 : 0.52)
}

function chaveDiametro(valor) {
  return formatarDiametro(valor)
}

function huesDaPaleta(mapa, paleta) {
  const huesUsados = Object.values(mapa).map((hex) => hexParaHsl(hex)[0])
  paleta.forEach((hex) => {
    const hue = hexParaHsl(hex)[0]
    if (!huesUsados.some((usado) => distHue(usado, hue) < 0.5)) {
      huesUsados.push(hue)
    }
  })
  return huesUsados
}

function proximaCorLivre(mapa, indice, escuro) {
  const paleta = paletaDiametro(escuro)
  const coresUsadas = new Set(
    Object.values(mapa).map((hex) => hex.toLowerCase())
  )
  const proxima = paleta.find((hex) => !coresUsadas.has(hex.toLowerCase()))
  return proxima || gerarCorExtra(indice, huesDaPaleta(mapa, paleta), escuro)
}

export function stopsDiametro(
  valores,
  { escuro = false, persistir = true } = {}
) {
  const mapa = { ...coresDiametroSalvas(escuro) }

  let mudou = false
  const stops = (valores || []).map((valor, i) => {
    const chave = chaveDiametro(valor)
    if (mapa[chave]) {
      return [valor, mapa[chave]]
    }

    const cor = proximaCorLivre(mapa, i, escuro)
    mapa[chave] = cor
    mudou = true
    return [valor, cor]
  })

  if (persistir && mudou) {
    gravarCoresDiametro(mapa, escuro)
  }

  return stops
}

export function escalaDiametroNasCamadas(camadas, { escuro = false } = {}) {
  const valores = diametrosNasCamadas(camadas)
  if (valores.length === 0) {
    return null
  }
  return stopsDiametro(valores, { escuro })
}

export function expressaoCorDiametro(stops, fallback = '#78909c') {
  if (!stops || stops.length === 0) {
    return fallback
  }

  const temDiametro = [
    'all',
    ['has', PROP_DIAMETRO],
    ['!=', ['to-string', ['get', PROP_DIAMETRO]], ''],
  ]

  const match = ['match', ['to-number', ['get', PROP_DIAMETRO]]]
  stops.forEach(([valor, cor]) => {
    match.push(valor, cor)
  })
  match.push(fallback)

  return ['case', temDiametro, match, fallback]
}
